import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home as HomeIcon, LogOut, User, Bell } from 'lucide-react';

const API_URL = getApiBaseUrl();

export const Navbar = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotificationCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      // Silently fail - notifications are non-critical
    }
  }, [token]);

  // Fetch notification count on mount and periodically
  useEffect(() => {
    if (user && token) {
      fetchNotificationCount();
      const interval = setInterval(fetchNotificationCount, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user, token, fetchNotificationCount]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNotificationsClick = () => {
    // Navigate to appropriate notifications page based on role
    const notificationPath = user?.role === 'tenant' 
      ? '/tenant/notifications'
      : user?.role === 'landlord' || user?.role === 'landlord_admin'
        ? '/landlord/notifications'
        : '/admin/notifications';
    navigate(notificationPath);
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Always visible */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="p-1.5 sm:p-2 bg-primary rounded-lg">
              <HomeIcon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <h1 className="text-lg sm:text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>

          {/* User Menu - Responsive */}
          <div className="flex items-center gap-2 sm:gap-6">
            {user && (
              <>
                {/* Properties Link - Hidden on mobile */}
                <Link to="/properties" className="hidden sm:block text-slate-700 hover:text-primary transition-colors font-medium">
                  Properties
                </Link>
                
                {/* Notifications */}
                <button 
                  onClick={handleNotificationsClick}
                  className="relative p-1.5 sm:p-2 text-slate-700 hover:text-primary transition-colors"
                  data-testid="navbar-notifications-btn"
                >
                  <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* User Info - Responsive */}
                <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-4 border-l">
                  {/* User details - Hidden on mobile */}
                  <div className="hidden md:block text-right">
                    <div className="text-sm font-semibold text-slate-900">{user.first_name} {user.last_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{user.role?.replace('_', ' ')}</div>
                  </div>
                  
                  {/* User Icon */}
                  <div className="p-1.5 sm:p-2 bg-slate-100 rounded-full">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
                  </div>
                  
                  {/* Logout */}
                  <button onClick={handleLogout} className="p-1.5 sm:p-2 text-slate-400 hover:text-red-500 transition-colors">
                    <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};